// components/DailyReport/DailyReportForm.tsx

import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useLocation } from 'react-router-dom'; // または react-router に応じた適切なインポート
import {
  TextField,
  Button,
  Grid,
  IconButton,
  Card,
  CardContent,
  Box,
  Typography,
  Autocomplete,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  ButtonBase,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Paper
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { Member } from '../../types/member';
import { buildMemberShortName, findMemberBySavedName } from '../../utils/memberDisplay';
import { dailyReportService } from '../../services/dailyReport';
import memberUnitPriceService, { EffectiveUnitPrice } from '../../services/memberUnitPrice';
import { ReportData, ConstructionInfo, ReportMemberData, ReportVehicleData, PrefillResponse } from '../../types/dailyReport';
import { PrefillBanner } from './PrefillBanner';
import { CalendarResponse } from '../../types/calendar';
import { ManpowerCategoryResponse } from '../../types/manpowerCategory';
import { MemberSelector } from '../Common/MemberSelector';
import { Vendor, VendorMember } from '../../types/vendor';
import { Car } from '../../types/car';
import { RouteCalculationRequest, RouteCalculationResponse } from '../../types/route';
import { RouteInfo } from '../../types/address';
import { NumberTextField } from '../Common/NumberTextField';
import { scheduleReportService } from '../../services/scheduleReportService';
import { UnreportedScheduleItem } from '../../types/commonSchedule';


export interface DailyReportFormProps {
  onSubmit: (data: ReportData, isCompleted: boolean) => Promise<{
    reportId?: number;
    success: boolean;
    error?: any;
  }>;
  onCancel: () => void;
  initialData?: ReportData | null;
  constructionInfo: ConstructionInfo;
  members: Member[];
  vendors: Vendor[];
  vendorMembers: VendorMember[];
  cars: Car[];
  loading?: boolean;
  routeInfo?: RouteInfo;
  // 新規モード時のみ渡される。前回日報からのプリフィル機能で使用
  prefillResponse?: PrefillResponse | null;
  fetchPrefill?: (reportId: number) => Promise<PrefillResponse | null>;
  convertMemberData?: (raw: any[]) => ReportMemberData[];
  convertVehicleData?: (raw: any[]) => ReportVehicleData[];
}

export const DailyReportForm: React.FC<DailyReportFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  constructionInfo,
  members,
  vendors,
  vendorMembers,
  cars,
  loading,
  routeInfo,
  prefillResponse,
  fetchPrefill,
  convertMemberData,
  convertVehicleData
}) => {
  // デフォルト値に本日の日付を設定
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const { register, control, handleSubmit, watch, setValue, getValues, formState: { errors } } = useForm<ReportData>({
    defaultValues: initialData || {
      date: today,
      // 前日の日報があれば prefill 経由で initialData が入るため、ここに来るのは「初日」のケース
      // → 工事の担当者を初期値として表示
      supervisor: constructionInfo?.supervisor || '',
      manPower: 0,
      mileage: 0,          // 必須項目に変更
      laborCost: 0,       // 必須項目に変更
      fuelCost: 0,        // 必須項目に変更
      vehicleCost: 0,     // 必須項目に変更
      tollLoadFee: 0,     // 必須項目に変更
      memberData: [],
      vehicleData: [],
      remarkData: []
      // isCompleted を削除
    }
  });


  // const [isConstructionCompleted, setIsConstructionCompleted] = useState(false);
  const watchMemberData = watch('memberData');
  const watchSuperviser = watch('supervisor');
  const [selectedMembers, setSelectedMembers] = useState<(Member | VendorMember)[]>([]);
  const [expandedVendors, setExpandedVendors] = useState<number[]>([]);
  const [manpowerCategories, setManpowerCategories] = useState<Map<number, ManpowerCategoryResponse['data']>>(new Map());
  // 社員の実効単価キャッシュ（キー: "memberId:YYYY-MM-DD"）。date を含むので日付が変われば別キー＝自動再取得。
  // 同一バッチ内の重複取得も防ぐため state ではなく ref で保持する。
  const effectivePricesRef = useRef<Map<string, EffectiveUnitPrice>>(new Map());
  const [isWorkingDay, setIsWorkingDay] = useState<boolean>(true);
  const selectedDate = watch('date');
  const [bulkSelectTabValue, setBulkSelectTabValue] = useState(0);
  const [bulkSearchQuery, setBulkSearchQuery] = useState('');
  const [dialogType, setDialogType] = useState<'company' | 'vendor' | 'supervisor' | 'car' | null>(null);
  const [destination, setDestination] = useState('');
  const [useETC, setUseETC] = useState(true);
  const [vehicleETCStatus, setVehicleETCStatus] = useState<Record<number, boolean>>({});
  const [totalLaborCost, setTotalLaborCost] = useState<number>(0);
  const [totalLaborHours, setTotalLaborHours] = useState<number>(0);
  const [totalOvertimeHours, setTotalOvertimeHours] = useState<number>(0);
  const [isConstructionCompleted, setIsConstructionCompleted] = useState(constructionInfo.isCompleted === 1);
  const [constructionDetailsExpanded, setConstructionDetailsExpanded] = useState(false);
  // 初日（新規 × prefill なし）に担当者を職長として作業員リストに追加する処理を行ったかどうか
  const [initialSupervisorAdded, setInitialSupervisorAdded] = useState(false);
  // メンバー詳細（勤務・時間外）の展開状態を field.id 単位で管理
  const [expandedMemberIds, setExpandedMemberIds] = useState<Set<string>>(new Set());
  const [memberAutoExpandDone, setMemberAutoExpandDone] = useState(false);
  // メンバー詳細リスト全体の表示/非表示（チップサマリで俯瞰、必要時のみ展開）
  const [memberListExpanded, setMemberListExpanded] = useState(false);
  // 「他のメンバーにも適用しますか？」モーダル
  const [applyToAllDialog, setApplyToAllDialog] = useState<{
    open: boolean;
    fieldName: 'workType' | 'overtimeHours';
    value: number;
    sourceIndex: number;
  } | null>(null);
  const [linkedSchedule, setLinkedSchedule] = useState<{ id: number; type: 'construction' | 'other' } | null>(null);
  const [isFromSchedule, setIsFromSchedule] = useState(false);

  // 前回日報プリフィル機能
  const [prefillSourceReportId, setPrefillSourceReportId] = useState<number | null>(
    prefillResponse?.prefill?.sourceReportId ?? null
  );
  const [prefillSourceReportDate, setPrefillSourceReportDate] = useState<string | null>(
    prefillResponse?.prefill?.sourceReportDate ?? null
  );
  const [prefillBannerVisible, setPrefillBannerVisible] = useState<boolean>(
    Boolean(prefillResponse?.prefill)
  );

  const handlePrefillSourceChange = async (reportId: number) => {
    if (!fetchPrefill || !convertMemberData || !convertVehicleData) return;
    const result = await fetchPrefill(reportId);
    if (!result || !result.prefill) return;
    setValue('supervisor', result.prefill.supervisor);
    replaceMembers(convertMemberData(result.prefill.memberData));
    replaceVehicles(convertVehicleData(result.prefill.vehicleData));
    setPrefillSourceReportId(result.prefill.sourceReportId);
    setPrefillSourceReportDate(result.prefill.sourceReportDate);
  };

  const handlePrefillClear = () => {
    setValue('supervisor', '');
    replaceMembers([]);
    replaceVehicles([]);
    setPrefillBannerVisible(false);
  };



  const {
    fields: memberFields,
    append: appendMember,
    remove: removeMember,
    replace: replaceMembers
  } = useFieldArray({
    control,
    name: "memberData"
  });

  const {
    fields: vehicleFields,
    append: appendVehicle,
    remove: removeVehicle,
    replace: replaceVehicles
  } = useFieldArray({
    control,
    name: "vehicleData"
  });

  const {
    fields: remarksFields,
    append: appendRemarks,
    remove: removeRemarks
  } = useFieldArray({
    control,
    name: "remarkData"
  });

  // バリデーションルールの追加
  const dateValidation = {
    required: '日付は必須です',
    validate: {
      notFuture: (value: string) => {
        // YYYY-MM-DD 形式の文字列を確実に現地時間の日付として解釈
        const [year, month, day] = value.split('-').map(Number);
        const inputDate = new Date(year, month - 1, day);

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        today.setMilliseconds(0);

        return inputDate <= today || '未来の日付は選択できません';
      }
    }
  };

  // URLパラメータからスケジュールデータを取得する処理
  useEffect(() => {
    const fetchScheduleData = async () => {
      // URLパラメータの取得
      const searchParams = new URLSearchParams(window.location.search);
      const scheduleId = searchParams.get('scheduleId');
      const scheduleType = searchParams.get('scheduleType');
      const supervisor = searchParams.get('supervisor');
  
      if (scheduleId && supervisor) {
        try {
          setIsFromSchedule(true);
  
          // scheduleReportServiceを使用してスケジュールデータを取得
          const { scheduleData, memberData, vehicleData } = await scheduleReportService.getScheduleDataFromParams(
            scheduleId,
            supervisor
          );
  
          // 日付をフォーマットして設定
          if (scheduleData && scheduleData.scheduleDate) {
            const isoDate = new Date(scheduleData.scheduleDate);
            const formattedDate = `${isoDate.getFullYear()}-${String(isoDate.getMonth() + 1).padStart(2, '0')}-${String(isoDate.getDate()).padStart(2, '0')}`;
            console.log('フォーマット後の日付:', formattedDate);
            setValue('date', formattedDate);
          }
  
          // 先に稼働日チェックを行う（単価計算に影響するため）
          let isWorkDay = true;
          if (scheduleData.scheduleDate) {
            try {
              isWorkDay = await dailyReportService.isWorkingDay(scheduleData.scheduleDate);
              setIsWorkingDay(isWorkDay);
            } catch (error) {
              console.error('稼働日チェックエラー:', error);
            }
          }
  
          // 職長を設定
          setValue('supervisor', supervisor);
  
          // 職長がメンバーリストに含まれているか確認
          const supervisorExists = memberData.some(m => m.memberName === supervisor);
          
          // メンバーデータのコピーを作成
          let updatedMemberData = [...memberData];
          
          // 職長がメンバーリストにない場合は追加
          if (!supervisorExists) {
            // 職長のメンバー情報を検索
            const supervisorMember = findMemberBySavedName(supervisor, members);
            
            if (supervisorMember) {
              // 新しいメンバーデータを作成
              const newSupervisorData: ReportMemberData = {
                memberOrder: 1, // 職長は常に先頭
                memberName: supervisor,
                isAllDate: 1,
                workType: 0,
                overtimeHours: 0,
                isCountTarget: 1.00,
                baseUnitPrice: 0, // 一時的な値、後で計算
                calculatedUnitPrice: 0, // 一時的な値、後で計算
                overtimePay: 0,
                nightShiftPay: 0,
                categoryId: supervisorMember.Manpower_Category_ID,
                memberId: supervisorMember.ID // 社員の職長 → 社員IDを保持
              };
              
              // 既存メンバーの順番を調整
              updatedMemberData = updatedMemberData.map(m => ({
                ...m,
                memberOrder: (m.memberOrder || 0) + 1
              }));
              
              // 職長を先頭に追加
              updatedMemberData = [newSupervisorData, ...updatedMemberData];
            }
          }
  
          // メンバー単価を取得して設定する関数
          const getProcessedMemberData = async (memberDataArray: ReportMemberData[]): Promise<ReportMemberData[]> => {
            // 更新されたメンバーデータを保持する配列
            const resultMemberData: ReportMemberData[] = [];
            // 同じリクエスト内でカテゴリIDによる単価計算結果をキャッシュ
            const categoryPriceCache: Record<number, number> = {};
          
            // 各メンバーの単価を計算して更新
            for (const member of memberDataArray) {
              // メンバーの単価を計算
              let unitPrice = 0;
              try {
                // 「業者名 - メンバー名」の形式かどうかで社員/外注を判定
                const isVendorMemberFormat = member.memberName.includes(' - ');

                if (isVendorMemberFormat) {
                  // 外注メンバー：従来どおりカテゴリ単価
                  const [vendorName, vName] = member.memberName.split(' - ');
                  const vendorMember = vendorMembers.find(
                    vm => vm.MemberName === vName && vm.VendorName === vendorName
                  );
                  const categoryId = member.categoryId ?? vendorMember?.Manpower_Category_ID ?? null;
                  if (categoryId != null) {
                    if (categoryPriceCache[categoryId] != null) {
                      unitPrice = categoryPriceCache[categoryId];
                      console.log(`外注メンバー[${member.memberName}]単価をキャッシュから取得: ${unitPrice}`);
                    } else {
                      unitPrice = await calculateUnitPrice({ categoryId });
                      categoryPriceCache[categoryId] = unitPrice;
                      console.log(`外注メンバー[${member.memberName}]単価計算結果: ${unitPrice}`);
                    }
                    member.categoryId = categoryId;
                  }
                  member.memberId = null;
                } else {
                  // 社員：適用日付き単価API（effective）で解決。社員IDは行の値→名前逆引きの順で解決
                  const companyMember = findMemberBySavedName(member.memberName, members);
                  const resolvedMemberId = member.memberId ?? companyMember?.ID ?? null;
                  const categoryId = member.categoryId ?? companyMember?.Manpower_Category_ID ?? null;

                  if (resolvedMemberId != null) {
                    // effective は「日付＋社員」依存。getEffectivePrice 側でキャッシュ済みのため categoryPriceCache は使わない
                    unitPrice = await calculateUnitPrice({ memberId: resolvedMemberId, categoryId });
                    member.memberId = resolvedMemberId;
                    if (categoryId != null) member.categoryId = categoryId;
                    console.log(`社員[${member.memberName}](ID:${resolvedMemberId})単価計算結果: ${unitPrice}`);
                  } else if (categoryId != null) {
                    // 社員IDが解決できない場合はカテゴリ単価にフォールバック
                    if (categoryPriceCache[categoryId] != null) {
                      unitPrice = categoryPriceCache[categoryId];
                    } else {
                      unitPrice = await calculateUnitPrice({ categoryId });
                      categoryPriceCache[categoryId] = unitPrice;
                    }
                    member.categoryId = categoryId;
                    console.log(`社員[${member.memberName}]単価（カテゴリfallback）: ${unitPrice}`);
                  }
                }
              } catch (error) {
                console.error(`メンバー[${member.memberName}]単価計算エラー:`, error);
              }
          
              // 単価を設定して配列に追加
              const updatedMember: ReportMemberData = {
                ...member,
                baseUnitPrice: unitPrice,
                calculatedUnitPrice: unitPrice
              };
          
              resultMemberData.push(updatedMember);
            }
          
            return resultMemberData;
          };
          
  
          // メンバーデータを処理（職長を含む）
          const processedMemberData = await getProcessedMemberData(updatedMemberData);
  
          // 処理済みのメンバーデータをフォームに設定
          setValue('memberData', processedMemberData);
  
          // 小計を再計算
          calculateTotals(processedMemberData);
  
          // 車両データを設定（ルート情報を反映）
          if (vehicleData && vehicleData.length > 0) {
            const updatedVehicleData = vehicleData.map(vehicle => ({
              ...vehicle,
              mileage: routeInfo ? Math.ceil(Number(routeInfo.Distance) / 1000) : 0,
              tollFee: routeInfo ? scheduleReportService.getVehicleTollFee(routeInfo, cars.find(c => c.ID === vehicle.carId)?.Category_ID) : 0
            }));
  
            setValue('vehicleData', updatedVehicleData);
          }
  
          // スケジュールと日報の関連付けのために情報を保持
          setLinkedSchedule({
            id: Number(scheduleId),
            type: scheduleType as 'construction' | 'other' || 'construction'
          });
  
        } catch (error) {
          console.error('スケジュールデータ取得エラー:', error);
        }
      }
    };
  
    fetchScheduleData();
  }, [routeInfo, cars, members, vendorMembers]); // 依存配列にvendorMembersも追加


  // 日報保存成功後にスケジュールとの関連付けを行う
  const handleSaveWithLinkage = async (data: ReportData, isCompleted: boolean) => {
    try {
      // 既存の日報保存処理を実行
      const result = await onSubmit(data, isCompleted);

      // 保存が成功し、レスポンスから日報IDが取得できる場合
      if (result && result.success && result.reportId && linkedSchedule) {
        // スケジュールと日報の関連付け
        await scheduleReportService.createScheduleReportLink(
          linkedSchedule.id,
          result.reportId
        );
      }

      return result;
    } catch (error) {
      console.error('日報保存エラー:', error);
      throw error;
    }
  };






  // 選択可能なメンバーをフィルタリングする関数
  const getAvailableMembers = (currentIndex: number) => {
    return members.filter(member => {
      const memberFullName = `${member.LastName} ${member.FirstName}`;
      return !watchMemberData.some((data, index) =>
        index !== currentIndex && data.memberName === memberFullName
      );
    });
  };


  const handleRemoveMember = (index: number) => {
    removeMember(index);
    // 削除後のメンバーデータで小計を再計算
    const updatedMemberData = getValues('memberData');
    calculateTotals(updatedMemberData);
  };

  useEffect(() => {
    const checkWorkingDay = async () => {
      if (selectedDate) {
        try {
          const isWorking = await dailyReportService.isWorkingDay(selectedDate);
          setIsWorkingDay(isWorking);
        } catch (error) {
          console.error('稼働日チェックエラー:', error);
        }
      }
    };
    checkWorkingDay();
  }, [selectedDate]);


  const calculateMemberUnitPrice = (
    baseUnitPrice: number,
    workType: number,
    isCountTarget: number,
    overtimeHours: number
  ) => {
    const isNightShift = workType === 1;
    let calculatedPrice = baseUnitPrice;

    // 夜勤の場合、基本単価を1.25倍
    if (isNightShift) {
      calculatedPrice *= 1.25;
    }

    // 時間外労働がある場合の追加計算
    if (overtimeHours > 0) {
      const overtimeRate = isNightShift ? 1.5 : 1.25;
      calculatedPrice += (baseUnitPrice / 8) * overtimeRate * overtimeHours;
    }

    // 人工による調整
    calculatedPrice *= isCountTarget;

    return calculatedPrice;
  };


  // 共通の calculateTotals 関数を定義（コンポーネントのトップレベルで）
  const calculateTotals = (memberData: any[]) => {
    const totals = memberData.reduce((acc, member, index) => {
      if (!member) return acc;

      // 値が存在しない場合のデフォルト値を設定
      const baseUnitPrice = Number(member.baseUnitPrice) || 0;
      const baseCount = Number(member.isCountTarget) || 0;
      const overtimeHours = Number(member.overtimeHours) || 0;
      const workType = Number(member.workType) || 0;
      const isNightShift = workType === 1;

      // 計算後単価を更新
      const calculatedPrice = calculateMemberUnitPrice(
        baseUnitPrice,
        workType,
        baseCount,
        overtimeHours
      );

      // NaNチェックを追加
      const safeCalculatedPrice = isNaN(calculatedPrice) ? 0 : calculatedPrice;

      // フォームの値を更新
      setValue(`memberData.${index}.calculatedUnitPrice`, safeCalculatedPrice);

      let baseAmount = baseUnitPrice * baseCount;
      if (isNightShift) {
        baseAmount *= 1.25;
      }

      let overtimeAmount = 0;
      if (overtimeHours > 0) {
        if (isNightShift) {
          overtimeAmount = baseUnitPrice / 8 * 1.5 * overtimeHours;
        } else {
          overtimeAmount = baseUnitPrice / 8 * 1.25 * overtimeHours;
        }
      }

      // NaNチェック
      const safeCost = isNaN(baseAmount + overtimeAmount) ? 0 : (baseAmount + overtimeAmount);
      const safeManPower = isNaN(baseCount) ? 0 : baseCount;
      const safeOvertimeHours = isNaN(overtimeHours) ? 0 : overtimeHours;

      return {
        cost: acc.cost + safeCost,
        manPower: acc.manPower + safeManPower,
        overtimeHours: acc.overtimeHours + safeOvertimeHours
      };
    }, {
      cost: 0,
      manPower: 0,
      overtimeHours: 0
    });

    setTotalLaborCost(totals.cost);
    setTotalLaborHours(totals.manPower);
    setTotalOvertimeHours(totals.overtimeHours);
  };


  // 日付変更時の useEffect
  useEffect(() => {
    const recalculateAllUnitPrices = async () => {
      try {
        const memberData = watch('memberData');
        if (!memberData || memberData.length === 0) {
          console.log('メンバーデータが空のため、単価計算をスキップします');
          return;
        }
        
        console.log('日付/稼働日変更による単価再計算を開始します');
        console.log('稼働日フラグ:', isWorkingDay);
        
        // すべての計算を非同期で並列実行し、結果を待機
        const updatedMemberData = [...memberData];
        const promises = memberData.map(async (data, index) => {
          const memberName = data.memberName;
          
          try {
            let unitPrice = 0;
            let resolvedMemberId: number | null = data.memberId ?? null;

            // 外注（「業者名 - メンバー名」形式）かどうかで処理を分ける
            const isVendorMemberFormat = memberName.includes(' - ');

            if (!isVendorMemberFormat) {
              // 社員：適用日付き単価API（effective）で再取得。
              // 社員IDは 行の memberId → 名前逆引き の順で解決（members 未ロード時も行の値で解決可能）
              const companyMember = findMemberBySavedName(memberName, members);
              resolvedMemberId = companyMember?.ID ?? data.memberId ?? null;
              if (resolvedMemberId != null) {
                unitPrice = await calculateUnitPrice({ memberId: resolvedMemberId, categoryId: data.categoryId ?? null });
              } else if (data.categoryId != null) {
                unitPrice = await calculateUnitPrice({ categoryId: data.categoryId });
              }
            } else {
              // 外注メンバー：従来どおりカテゴリ単価
              resolvedMemberId = null;
              const parts = memberName.split(' - ');
              if (parts.length === 2) {
                const [vendorName, vName] = parts;
                const vendorMember = vendorMembers.find(
                  vm => vm.MemberName === vName && vm.VendorName === vendorName
                );
                const categoryId = data.categoryId ?? vendorMember?.Manpower_Category_ID ?? null;
                if (categoryId != null) {
                  unitPrice = await calculateUnitPrice({ categoryId });
                }
              }
            }

            // 計算結果を格納
            updatedMemberData[index] = {
              ...data,
              memberId: resolvedMemberId,
              baseUnitPrice: unitPrice,
              calculatedUnitPrice: unitPrice
            };
          } catch (err) {
            console.error(`メンバー[${memberName}]の単価再計算エラー:`, err);
          }
        });
        
        // すべての非同期処理の完了を待機
        await Promise.all(promises);
        
        // フォームに一括で値を設定
        setValue('memberData', updatedMemberData);
        
        // 小計を再計算
        calculateTotals(updatedMemberData);
        
        console.log('単価再計算が完了しました');
      } catch (error) {
        console.error('単価再計算エラー:', error);
      }
    };
  
    // 稼働日情報が変更されたときのみ実行
    if (memberFields.length > 0) {
      recalculateAllUnitPrices();
    }
  }, [isWorkingDay, selectedDate]);
  

  // メンバーデータ変更時の useEffect
  // useEffect(() => {
  //   const subscription = watch((value, { name }) => {
  //     if (name?.startsWith('memberData')) {
  //       const memberData = getValues('memberData');
  //       calculateTotals(memberData);
  //     }
  //   });

  //   return () => subscription.unsubscribe();
  // }, [watch, getValues]);


  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name?.startsWith('memberData')) {
        const memberData = getValues('memberData');

        // 特定のフィールドの変更時のみ再計算
        const fieldsTriggeringRecalculation = [
          'workType',
          'isCountTarget',
          'overtimeHours'
        ];

        const shouldRecalculate = fieldsTriggeringRecalculation.some(field =>
          name?.includes(field)
        );

        if (shouldRecalculate) {
          calculateTotals(memberData);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, getValues]);


  // メンバー詳細（勤務・時間外）の自動展開
  // 編集/prefill モードで dayShift 以外 or 残業ありのメンバーは初回ロード時に展開状態にする
  useEffect(() => {
    if (memberAutoExpandDone) return;
    if (memberFields.length === 0) return;

    const memberData = getValues('memberData');
    const idsToExpand = new Set<string>();
    memberFields.forEach((field, idx) => {
      const m = memberData?.[idx];
      if (!m) return;
      const workType = Number(m.workType ?? 0);
      const overtimeHours = Number(m.overtimeHours ?? 0);
      // 夜勤(=1) または 残業時間≠0 なら詳細展開
      if (workType !== 0 || overtimeHours !== 0) {
        idsToExpand.add(field.id);
      }
    });
    setExpandedMemberIds(idsToExpand);
    setMemberAutoExpandDone(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberFields.length]);

  const toggleMemberExpanded = (fieldId: string) => {
    setExpandedMemberIds(prev => {
      const next = new Set(prev);
      if (next.has(fieldId)) next.delete(fieldId);
      else next.add(fieldId);
      return next;
    });
  };

  // 勤務 or 時間外 の変更時に「他メンバーにも適用するか？」モーダルを表示
  const promptApplyToAll = (
    fieldName: 'workType' | 'overtimeHours',
    newValue: number,
    sourceIndex: number
  ) => {
    if (memberFields.length <= 1) return; // 1人しかいない場合は不要
    // 全員既に同じ値ならスキップ
    const allSame = memberFields.every((_, i) => {
      if (i === sourceIndex) return true;
      const v = getValues(`memberData.${i}.${fieldName}`);
      return Number(v) === Number(newValue);
    });
    if (allSame) return;
    setApplyToAllDialog({ open: true, fieldName, value: newValue, sourceIndex });
  };

  const handleApplyToAllConfirm = () => {
    if (!applyToAllDialog) return;
    const { fieldName, value, sourceIndex } = applyToAllDialog;
    memberFields.forEach((_, i) => {
      if (i === sourceIndex) return;
      setValue(`memberData.${i}.${fieldName}`, value);
    });
    // 全体再計算
    const memberData = getValues('memberData');
    calculateTotals(memberData);
    setApplyToAllDialog(null);
  };

  const handleApplyToAllCancel = () => {
    setApplyToAllDialog(null);
  };

  // 職長の選択を監視
  useEffect(() => {
    if (!watchSuperviser) {
      const superviserIndex = memberFields.findIndex(field =>
        field.memberName === watchSuperviser
      );
      if (superviserIndex !== -1) {
        handleRemoveMember(superviserIndex);
      }
    }
  }, [watchSuperviser]);


  const handleMemberSelect = async (index: number, selectedMember: Member | null) => {
    if (selectedMember) {
      const unitPrice = await calculateUnitPrice(selectedMember);
      setValue(`memberData.${index}.memberName`, `${selectedMember.LastName} ${selectedMember.FirstName}`);
      setValue(`memberData.${index}.memberId`, selectedMember.ID); // 社員 → 社員IDを保持
      setValue(`memberData.${index}.baseUnitPrice`, unitPrice);
    } else {
      setValue(`memberData.${index}.memberName`, '');
      setValue(`memberData.${index}.memberId`, null);
      setValue(`memberData.${index}.baseUnitPrice`, 0);
    }
  };


  // メンバー追加の共通処理を関数化
  const handleMemberAdd = async (
    member: Member | VendorMember,
    displayName: string
  ) => {
    const unitPrice = await calculateUnitPrice(member);
    // undefined を null に変換
    const categoryId = 'Manpower_Category_ID' in member ? (member.Manpower_Category_ID || null) : null;
    // 社員（Member: VendorID を持たない）のときだけ社員IDを保持。外注は null
    const memberId = !('VendorID' in member) && 'ID' in member ? member.ID : null;

    const newMemberData: ReportMemberData = {
      memberOrder: memberFields.length + 1,
      memberName: displayName,
      isAllDate: 1,
      workType: 0,
      overtimeHours: 0,
      isCountTarget: 1.00,
      baseUnitPrice: unitPrice,
      calculatedUnitPrice: unitPrice,
      overtimePay: 0,
      nightShiftPay: 0,
      categoryId: categoryId,
      memberId: memberId
    };
  
    if (memberFields.length === 0) {
      setValue('memberData', [newMemberData], {
        shouldValidate: true,
        shouldDirty: true
      });
    } else {
      appendMember(newMemberData);
    }
  
    const memberData = getValues('memberData');
    calculateTotals(memberData);
  };


  // 初日（新規 × prefill なし）の場合、工事担当者を職長として作業員リストに自動追加
  // 編集モード／prefill 経由のケースでは memberData に既に職長が含まれているのでスキップ
  useEffect(() => {
    if (initialSupervisorAdded) return;
    if (initialData) return;                    // 編集モードはスキップ
    if (prefillResponse?.prefill) return;       // prefill モードはスキップ（前日の職長が既に組み込まれる）
    if (!members || members.length === 0) return; // members 読み込み待ち

    const currentSupervisor = getValues('supervisor');
    if (!currentSupervisor) {
      setInitialSupervisorAdded(true);
      return;
    }

    // 既に作業員リストにいれば何もしない（職長選択ダイアログ経由で先に追加済みの場合など）
    const alreadyInList = memberFields.some(field => field.memberName === currentSupervisor);
    if (alreadyInList) {
      setInitialSupervisorAdded(true);
      return;
    }

    // 担当者の Member を検索して作業員リストに追加
    const supervisorMember = findMemberBySavedName(currentSupervisor, members);
    if (supervisorMember) {
      handleMemberAdd(supervisorMember, currentSupervisor);
    }
    setInitialSupervisorAdded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, initialData, prefillResponse, initialSupervisorAdded]);


  // メンバー選択時の処理
  const handleCompanyMemberSelect = async (member: Member) => {
    const displayName = buildMemberShortName(member, members);
    const isAdded = memberFields.some(field => field.memberName === displayName);

    if (isAdded) {
      const indexToRemove = memberFields.findIndex(field =>
        field.memberName === displayName
      );
      if (indexToRemove !== -1) {
        handleRemoveMember(indexToRemove);
      }
    } else {
      await handleMemberAdd(member, displayName);
    }
  };


  const handleVendorMemberSelect = async (member: VendorMember, vendorName: string) => {
    const displayName = `${vendorName} - ${member.MemberName}`;
    const isAdded = memberFields.some(field =>
      field.memberName === displayName
    );

    if (isAdded) {
      const indexToRemove = memberFields.findIndex(field =>
        field.memberName === displayName
      );
      if (indexToRemove !== -1) {
        handleRemoveMember(indexToRemove);
      }
    } else {
      await handleMemberAdd(member, displayName);
    }
  };


  // 日付を YYYY-MM-DD に整形（effective API / 稼働日判定と同じ「日報の日付」を渡す）
  const toYmd = (date: string): string => {
    if (!date) return date;
    if (date.includes('T')) {
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    return date;
  };

  // 渡されたメンバーから「社員ID」と「カテゴリID」を判定する
  // - 社員(Member): ID を持ち VendorID を持たない → memberId を採用
  // - 外注(VendorMember): VendorID を持つ → memberId は null（従来のカテゴリ単価のまま）
  // - { categoryId, memberId } オブジェクト（日報データ由来）: 明示指定を優先
  const resolvePriceKeys = (
    member: Member | VendorMember | { categoryId?: number | null; memberId?: number | null }
  ): { memberId: number | null; categoryId: number | null } => {
    // 明示的に memberId / categoryId を持つプレーンオブジェクト（Manpower_Category_ID は持たない）
    if (!('Manpower_Category_ID' in member) && ('memberId' in member || 'categoryId' in member)) {
      const m = member as { categoryId?: number | null; memberId?: number | null };
      return { memberId: m.memberId ?? null, categoryId: m.categoryId ?? null };
    }
    // 外注メンバー
    if ('VendorID' in member) {
      return { memberId: null, categoryId: member.Manpower_Category_ID ?? null };
    }
    // 社員（Member）
    if ('ID' in member) {
      return { memberId: (member as Member).ID ?? null, categoryId: (member as Member).Manpower_Category_ID ?? null };
    }
    return { memberId: null, categoryId: null };
  };

  // 社員の実効単価を取得（キャッシュ付き）。キーに日付を含むため日付が変われば再取得される
  const getEffectivePrice = async (memberId: number, ymd: string): Promise<EffectiveUnitPrice> => {
    const key = `${memberId}:${ymd}`;
    const cached = effectivePricesRef.current.get(key);
    if (cached) return cached;
    const data = await memberUnitPriceService.getEffective(memberId, ymd);
    effectivePricesRef.current.set(key, data);
    return data;
  };

  const calculateUnitPrice = async (
    member: Member | VendorMember | { categoryId?: number | null; memberId?: number | null }
  ): Promise<number> => {
    try {
      const { memberId, categoryId } = resolvePriceKeys(member);

      // 社員 かつ 日報の日付が確定していれば、適用日付き単価API（effective）で解決
      // date には必ず「その日報の日付」を渡す（今日ではない）。個人別単価が無ければバック側でカテゴリ単価にフォールバックされる
      if (memberId != null && selectedDate) {
        try {
          const ymd = toYmd(selectedDate);
          const eff = await getEffectivePrice(memberId, ymd);
          const unitPrice = isWorkingDay ? eff.Weekday_Unit_Price : eff.Holiday_Unit_Price;
          console.log(`社員ID ${memberId} の実効単価: ${unitPrice} (source:${eff.source}, 日付:${ymd}, 稼働日:${isWorkingDay})`);
          return unitPrice;
        } catch (error) {
          // 400/404 等は従来のカテゴリ単価にフォールバック
          console.warn(`社員ID ${memberId} の実効単価取得に失敗、カテゴリ単価にフォールバックします:`, error);
        }
      }

      // 従来のカテゴリ単価（外注 / memberId 不明 / effective 失敗時）
      if (!categoryId) {
        console.warn('カテゴリIDが見つかりません:', member);
        return 0;
      }

      try {
        // カテゴリ情報をキャッシュから取得または新規取得
        if (!manpowerCategories.has(categoryId)) {
          console.log(`カテゴリID ${categoryId} の単価情報を取得します`);
          const response = await dailyReportService.getManpowerCategory(categoryId);
          const category = response.data;
          
          // カテゴリ情報が正しく取得できたか確認
          if (!category) {
            console.warn(`カテゴリID ${categoryId} の情報取得に失敗しました`);
            return 0;
          }
          
          // ここでnumber型であることを確保した値を使用
          const safeId: number = categoryId; // categoryIdはnullでないことが確認済み
          setManpowerCategories(prev => {
            const newMap = new Map(prev);
            newMap.set(safeId, category);
            return newMap;
          });
          
          // 稼働日に応じた単価を返す
          const unitPrice = isWorkingDay ? category.Weekday_Unit_Price : category.Holiday_Unit_Price;
          console.log(`カテゴリID ${categoryId} の単価: ${unitPrice} (稼働日: ${isWorkingDay})`);
          return unitPrice;
        }
      
        // キャッシュから取得
        const category = manpowerCategories.get(categoryId);
        if (!category) {
          console.warn(`カテゴリID ${categoryId} の情報が見つかりません`);
          return 0;
        }
        
        const unitPrice = isWorkingDay ? category.Weekday_Unit_Price : category.Holiday_Unit_Price;
        console.log(`カテゴリID ${categoryId} の単価: ${unitPrice} (稼働日: ${isWorkingDay})`);
        return unitPrice;
      } catch (error) {
        console.error(`カテゴリID ${categoryId} の単価計算エラー:`, error);
        return 0;
      }
    } catch (error) {
      console.error(`単価計算エラー:`, error);
      return 0;
    }
  };
  
  
  

  // チェックボックスの状態管理
  // const handleMemberCheckboxChange = async (
  //   member: Member | VendorMember, 
  //   checked: boolean
  // ) => {
  //   if (checked) {
  //     setSelectedMembers(prev => [...prev, member]);
  //   } else {
  //     setSelectedMembers(prev => prev.filter(m => m.ID !== member.ID));
  //   }
  // };

  // Member型とVendorMember型を区別するための型ガード関数
  const isMember = (member: Member | VendorMember): member is Member => {
    return 'LastName' in member && 'FirstName' in member;
  };

  // 外注業者選択時にアコーディオンを展開
  const handleOpenVendorDialog = () => {
    // 現在選択されているメンバーの業者IDを取得
    const selectedVendorIds = memberFields
      .map(field => {
        if (field.memberName.includes(' - ')) {
          const [vendorName] = field.memberName.split(' - ');
          const vendor = vendors.find(v => v.VendorName === vendorName);
          return vendor?.ID;
        }
        return null;
      })
      .filter((id): id is number => id != null);

    // 重複を除去（代替方法）
    const uniqueVendorIds = Array.from(
      selectedVendorIds.reduce((acc, id) => {
        acc.add(id);
        return acc;
      }, new Set<number>())
    );

    // または、より単純な方法
    // const uniqueVendorIds = selectedVendorIds.filter(
    //   (id, index) => selectedVendorIds.indexOf(id) === index
    // );

    setExpandedVendors(uniqueVendorIds);
    setDialogType('vendor');
  };

  const handleCloseVendorDialog = () => {
    setExpandedVendors([]);
    setDialogType(null);
  };

  // const handleBulkAdd = async () => {
  //   for (const member of selectedMembers) {
  //     const unitPrice = await calculateUnitPrice(member);
  //     const memberName = isMember(member) ? 
  //       `${member.LastName} ${member.FirstName}` : 
  //       member.MemberName;

  //     appendMember({
  //       memberOrder: memberFields.length + 1,
  //       memberName: memberName,
  //       unitPrice: unitPrice,
  //       workType: 0,
  //       overtimeHours: 0
  //     });
  //   }
  //   setSelectedMembers([]);
  //   setDialogType(null);
  // };

  // 検索フィルター用の関数
  const filteredCompanyMembers = members.filter(member => {
    const searchText = `${member.LastName}${member.FirstName}${member.staff_initial}`.toLowerCase();
    return searchText.includes(bulkSearchQuery.toLowerCase());
  });

  const filteredVendorMembers = vendorMembers.filter(member => {
    const searchText = member.MemberName.toLowerCase();
    return searchText.includes(bulkSearchQuery.toLowerCase());
  });


  const handleCarSelect = async (car: Car) => {
    const isAdded = vehicleFields.some(field => field.carId === car.ID);

    if (isAdded) {
      const indexToRemove = vehicleFields.findIndex(field =>
        field.carId === car.ID
      );
      if (indexToRemove !== -1) {
        removeVehicle(indexToRemove);
      }
    } else {
      if (!canAddMoreVehicles) {
        return;
      }

      // 車両カテゴリに応じた料金を設定
      let tollFee = 0;
      if (routeInfo) {
        switch (car.Category_ID) {
          case 1: // 大型
            tollFee = routeInfo.Medium_Vehicle_Toll;
            break;
          case 2: // 中型
            tollFee = routeInfo.Medium_Vehicle_Toll;
            break;
          case 3: // 普通
            tollFee = routeInfo.Standard_Vehicle_Toll;
            break;
        }
      }

      const newVehicleData: ReportVehicleData = {
        vehicleOrder: vehicleFields.length + 1,
        carId: car.ID,
        unitPrice: car.Unit_Price || 0,
        mileage: routeInfo ? Math.ceil(Number(routeInfo.Distance) / 1000) : 0,  // ルート情報から走行距離を設定
        tollFee: tollFee  // 車両カテゴリに応じた料金を設定
      };

      if (vehicleFields.length === 0) {
        setValue('vehicleData', [newVehicleData], {
          shouldValidate: true,
          shouldDirty: true
        });
      } else {
        appendVehicle(newVehicleData);
      }
    }
  };

  // 有料道路料金計算用のヘルパー関数
  const calculateTollFee = (categoryId: number): number => {
    if (!routeInfo) return 0;

    switch (categoryId) {
      case 1: return routeInfo.Medium_Vehicle_Toll;
      case 2: return routeInfo.Medium_Vehicle_Toll;
      case 3: return routeInfo.Standard_Vehicle_Toll;
      default: return 0;
    }
  };

  const handleBulkMileageChange = (value: number) => {
    if (dialogType === 'car') return;
    setValue('mileage', value);
  };


  // 選択できる車両は最大５台まで
  const canAddMoreVehicles = vehicleFields.length < 10;

  const onSubmitHandler = (data: ReportData) => {
    // 走行距離の集計（往復）
    const totalMileage = data.vehicleData.reduce((sum, vehicle) => {
      const mileage = Number(vehicle.mileage) || 0;
      return sum + mileage * 2;  // 片道→往復
    }, 0);

    // 有料道路料金の集計（往復）
    const totalTollFee = data.vehicleData.reduce((sum, vehicle) => {
      const tollFee = Number(vehicle.tollFee) || 0;
      return sum + tollFee * 2;  // 片道→往復
    }, 0);

    // 車両費の集計
    const totalVehicleCost = data.vehicleData.reduce((sum, vehicle) => {
      const unitPrice = Number(vehicle.unitPrice) || 0;
      return sum + unitPrice;
    }, 0);

    // 燃料費の計算（往復総距離 × 30円）
    const totalFuelCost = totalMileage * 30;

    // 送信データの作成
    const submitData: ReportData = {
      ...data,
      constructionId: constructionInfo.constructionId,  // 工事ID
      mileage: totalMileage,                            // 総走行距離（往復）
      laborCost: totalLaborCost,                       // 人件費（小計から）
      manPower: totalLaborHours,                       // 人工（小計から）
      fuelCost: totalFuelCost,                         // 燃料費
      vehicleCost: totalVehicleCost,                   // 車両費
      tollLoadFee: totalTollFee                        // 有料道路料金（往復）
    };

    // 親コンポーネントのonSubmitを呼び出し
    onSubmit(submitData, isConstructionCompleted);
  };

  // 共通のセクションスタイル
  const sectionStyle = {
    p: 3,
    mb: 3,
    bgcolor: 'background.default',
    borderRadius: 1
  };

  // セクションヘッダーのスタイル
  const sectionHeaderStyle = {
    mb: 3,
    pb: 1,
    borderBottom: 2,
    borderColor: 'primary.main',
    color: 'primary.main',
    fontWeight: 'medium'
  };



  return (
    <form
      // 入力欄でのEnterによる意図しないフォーム送信（保存ボタン発火）を防ぐ。
      // テキストエリアでの改行と、ボタンへフォーカス中の意図的なEnter送信は許可する。
      onKeyDown={(e) => {
        const target = e.target as HTMLElement;
        if (
          e.key === 'Enter' &&
          target.tagName !== 'TEXTAREA' &&
          target.tagName !== 'BUTTON'
        ) {
          e.preventDefault();
        }
      }}
      onSubmit={handleSubmit((data: ReportData) => {
      {
        isFromSchedule && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
              borderRadius: 1
            }}
          >
            <Typography variant="body1">
              スケジュールから取得したデータを使用しています。
              職長情報やメンバー・車両データはスケジュールから自動設定されています。
            </Typography>
          </Box>
        )
      }

      // 送信データの作成
      // 走行距離の集計（往復）
      const totalMileage = data.vehicleData.reduce((sum, vehicle) => {
        return sum + (vehicle.mileage || 0) * 2;  // 片道→往復
      }, 0);

      // 有料道路料金の集計（往復）
      const totalTollFee = data.vehicleData.reduce((sum, vehicle) => {
        return sum + (vehicle.tollFee || 0) * 2;  // 片道→往復
      }, 0);

      // 車両費の集計
      const totalVehicleCost = data.vehicleData.reduce((sum, vehicle) => {
        return sum + (vehicle.unitPrice || 0);
      }, 0);

      // 燃料費の計算（往復総距離 × 30円）
      const totalFuelCost = totalMileage * 30;

      // 送信データの作成
      const submitData: ReportData = {
        ...data,
        constructionId: constructionInfo.constructionId,  // 工事ID
        mileage: totalMileage,                            // 総走行距離（往復）
        laborCost: totalLaborCost,                       // 人件費（小計から）
        manPower: totalLaborHours,                       // 人工（小計から）
        fuelCost: totalFuelCost,                         // 燃料費
        vehicleCost: totalVehicleCost,                   // 車両費
        tollLoadFee: totalTollFee                        // 有料道路料金（往復）
      };

      // onSubmit(submitData, isConstructionCompleted);

      // スケジュールとの連携がある場合
      if (linkedSchedule) {
        handleSaveWithLinkage(submitData, isConstructionCompleted);
      } else {
        // 通常の保存処理
        onSubmit(submitData, isConstructionCompleted);
      }

    })}>
      {prefillBannerVisible && prefillResponse?.prefill && prefillSourceReportId !== null && prefillSourceReportDate !== null && (
        <PrefillBanner
          sourceReportId={prefillSourceReportId}
          sourceReportDate={prefillSourceReportDate}
          availableReports={prefillResponse.availableReports}
          onChangeSource={handlePrefillSourceChange}
          onClear={handlePrefillClear}
        />
      )}
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* 工事情報の表示を追加 */}
        <Grid item xs={12}>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.5, sm: 3 },
              mb: { xs: 2, sm: 3 },
              bgcolor: 'background.default',
              borderRadius: 1
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: { xs: 2, sm: 3 },
                pb: 1,
                borderBottom: 2,
                borderColor: 'primary.main',
                color: 'primary.main',
                fontWeight: 'medium'
              }}
            >
              工事情報
            </Typography>

            {/* 常時表示: 現場名 */}
            <Box sx={{
              p: { xs: 1.25, sm: 2 },
              bgcolor: 'grey.50',
              borderRadius: 1,
              mb: { xs: 1, sm: 1.5 }
            }}>
              <Typography variant="body2" color="text.secondary">現場名</Typography>
              <Typography variant="body1">{constructionInfo.siteName}</Typography>
            </Box>

            {/* 詳細トグルボタン */}
            <Button
              fullWidth
              size="small"
              onClick={() => setConstructionDetailsExpanded(!constructionDetailsExpanded)}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    transform: constructionDetailsExpanded ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.3s'
                  }}
                />
              }
              sx={{ mb: { xs: 1, sm: 1.5 }, color: 'text.secondary', fontWeight: 'normal' }}
            >
              {constructionDetailsExpanded ? '詳細を隠す' : '詳細を表示（顧客名・見積金額・担当者・住所）'}
            </Button>

            {/* 折りたたみ部分: 顧客名・見積金額・担当者・住所 */}
            <Collapse in={constructionDetailsExpanded}>
              <Box sx={{
                p: { xs: 1.25, sm: 2 },
                bgcolor: 'grey.50',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 1, sm: 1.5 },
                mb: { xs: 1, sm: 1.5 }
              }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">顧客名</Typography>
                  <Typography variant="body1">{constructionInfo.customerName}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">見積金額</Typography>
                  <Typography variant="body1">
                    ¥{constructionInfo.estimatedAmount.toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">担当者</Typography>
                  <Typography variant="body1">{constructionInfo.supervisor}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">住所</Typography>
                  <Typography variant="body1">{constructionInfo.address}</Typography>
                </Box>
              </Box>
            </Collapse>

            {/* 常時表示: 工事完了チェックボックス */}
            <Box sx={{
              borderTop: 1,
              borderColor: 'divider',
              pt: { xs: 1.5, sm: 2 }
            }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isConstructionCompleted}
                    onChange={(e) => setIsConstructionCompleted(e.target.checked)}
                    color="primary"
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {isConstructionCompleted ? '工事完了' : '工事未完了'}
                  </Typography>
                }
              />
            </Box>
          </Paper>
        </Grid>
        {/* 基本情報 */}
        <Grid item xs={12}>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.5, sm: 3 },
              mb: { xs: 2, sm: 3 },
              bgcolor: 'background.default',
              borderRadius: 1
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                pb: 1,
                borderBottom: 2,
                borderColor: 'primary.main',
                color: 'primary.main',
                fontWeight: 'medium'
              }}
            >
              基本情報
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  {...register('date', dateValidation)}
                  type="date"
                  fullWidth
                  label="日付"
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      color: 'primary.main',
                      fontWeight: 'medium'
                    }
                  }}
                  error={!!errors.date}
                  helperText={errors.date?.message}
                  inputProps={{
                    max: today,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="職長"
                  value={watch('supervisor')}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setDialogType('supervisor')}
                        >
                          <PersonSearchIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  onClick={() => setDialogType('supervisor')}
                  error={!!errors?.supervisor}
                  helperText={errors?.supervisor?.message || '職長の選択は必須です'}
                  {...register('supervisor', { required: '職長を選択してください' })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }
                  }}
                />
              </Grid>

              {/* 職長選択用のダイアログ */}
              <Dialog
                open={dialogType === 'supervisor'}
                onClose={() => setDialogType(null)}
                fullWidth
                maxWidth="sm"
                disableRestoreFocus
              >
                <DialogTitle sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  mb: 2
                }}>
                  職長選択
                </DialogTitle>
                <DialogContent dividers>
                  <Grid container spacing={1}>
                    {[...filteredCompanyMembers]
                      .sort((a, b) => (a.ID ?? 0) - (b.ID ?? 0))
                      .map((member) => {
                        const isSelected = watch('supervisor') === buildMemberShortName(member, members);
                        return (
                          <Grid item xs={4} key={`supervisor-${member.ID}`}>
                            <Button
                              fullWidth
                              variant={isSelected ? "contained" : "outlined"}
                              color={isSelected ? "success" : "primary"}
                              onClick={async () => {
                                // 現在の職長を取得
                                const currentSuperviser = watch('supervisor');

                                if (currentSuperviser) {
                                  const superviserIndex = memberFields.findIndex(field =>
                                    field.memberName === currentSuperviser
                                  );
                                  if (superviserIndex !== -1) {
                                    removeMember(superviserIndex);
                                  }
                                }

                                const supervisorName = buildMemberShortName(member, members);
                                setValue('supervisor', supervisorName);
                                await handleMemberAdd(member, supervisorName);
                                setDialogType(null);
                              }}
                              sx={{
                                height: '60px',
                                whiteSpace: 'normal',
                                lineHeight: 1.2,
                                textTransform: 'none',
                                ...(isSelected && {
                                  fontWeight: 'bold',
                                  boxShadow: 1,
                                }),
                              }}
                            >
                              <Box>
                                <Typography variant="body1">{buildMemberShortName(member, members)}</Typography>
                              </Box>
                            </Button>
                          </Grid>
                        );
                      })}
                  </Grid>
                </DialogContent>
                <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2 }}>
                  <Button
                    onClick={() => setDialogType(null)}
                    variant="outlined"
                  >
                    閉じる
                  </Button>
                </DialogActions>
              </Dialog>
            </Grid>
          </Paper>
        </Grid>

        {/* メンバー情報セクション */}
        <Grid item xs={12}>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.5, sm: 3 },
              mb: { xs: 2, sm: 3 },
              bgcolor: 'background.default',
              borderRadius: 1
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                pb: 1,
                borderBottom: 2,
                borderColor: 'primary.main',
                color: 'primary.main',
                fontWeight: 'medium'
              }}
            >
              メンバー情報
            </Typography>

            {/* メンバー選択セクション */}
            <Paper
              variant="outlined"
              sx={{
                mb: 3,
                p: 2,
                bgcolor: 'grey.50'
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 2,
                  fontWeight: 'medium'
                }}
              >
                メンバー選択
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setDialogType('company')}
                    startIcon={<PersonAddIcon />}
                    sx={{
                      py: 1.5,
                      bgcolor: 'background.paper',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    配川工業
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleOpenVendorDialog}
                    startIcon={<GroupAddIcon />}
                    sx={{
                      py: 1.5,
                      bgcolor: 'background.paper',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    外注
                  </Button>
                </Grid>
              </Grid>

              {/* メンバーサマリチップ: 配川工業=青系, 外注=オレンジ系（控えめなトーン） */}
              {memberFields.length > 0 && (() => {
                const vendorCount = memberFields.filter(f => f.memberName?.includes(' - ')).length;
                const companyCount = memberFields.length - vendorCount;
                return (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.75 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        合計: {memberFields.length}人
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        （配川工業 {companyCount} / 外注 {vendorCount}）
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {memberFields.map((field, index) => {
                    const isVendor = field.memberName?.includes(' - ');
                    // 外注は "ベンダー名 - メンバー名" 形式 → メンバー名のみ表示
                    const displayLabel = isVendor
                      ? field.memberName.split(' - ').slice(1).join(' - ')
                      : field.memberName;
                    return (
                      <Chip
                        key={field.id}
                        label={displayLabel}
                        size="small"
                        variant="filled"
                        onDelete={() => handleRemoveMember(index)}
                        sx={{
                          fontWeight: 'medium',
                          bgcolor: isVendor ? '#fff3e0' : '#e3f2fd',          // 薄オレンジ / 薄ブルー
                          color: isVendor ? '#e65100' : '#0d47a1',             // 文字色は濃いめでコントラスト確保
                          border: 1,
                          borderColor: isVendor ? '#ffcc80' : '#90caf9',
                          '& .MuiChip-deleteIcon': {
                            color: isVendor ? 'rgba(230, 81, 0, 0.5)' : 'rgba(13, 71, 161, 0.5)',
                            '&:hover': {
                              color: isVendor ? '#e65100' : '#0d47a1'
                            }
                          }
                        }}
                      />
                    );
                  })}
                    </Box>
                  </Box>
                );
              })()}
            </Paper>

            {/* メンバー選択ダイアログ - 自社メンバー */}
            <Dialog
              open={dialogType === 'company'}
              onClose={() => setDialogType(null)}
              fullWidth
              maxWidth="sm"
              disableRestoreFocus
            >
              <DialogTitle sx={{
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'grey.50',
                px: 3,
                py: 2
              }}>
                配川工業メンバー選択
              </DialogTitle>
              <DialogContent sx={{ p: 3 }}>
                <Grid container spacing={1.5}>
                  {[...filteredCompanyMembers]
                    .sort((a, b) => (a.ID ?? 0) - (b.ID ?? 0))
                    .map((member) => {
                      const isAdded = memberFields.some((field) =>
                        field.memberName === buildMemberShortName(member, members)
                      );

                      return (
                        <Grid item xs={4} key={`company-${member.ID}`}>
                          <Button
                            fullWidth
                            variant={isAdded ? "contained" : "outlined"}
                            color={isAdded ? "success" : "primary"}
                            onClick={() => handleCompanyMemberSelect(member)}
                            sx={{
                              height: '60px',
                              whiteSpace: 'normal',
                              lineHeight: 1.2,
                              textTransform: 'none',
                              ...(isAdded && {
                                fontWeight: 'bold',
                                boxShadow: 1,
                              }),
                            }}
                          >
                            <Box>
                              <Typography variant="body1">{buildMemberShortName(member, members)}</Typography>
                            </Box>
                          </Button>
                        </Grid>
                      );
                    })}
                </Grid>
              </DialogContent>
              <DialogActions sx={{
                borderTop: 1,
                borderColor: 'divider',
                px: 3,
                py: 2
              }}>
                <Button
                  onClick={() => setDialogType(null)}
                  variant="outlined"
                >
                  閉じる
                </Button>
              </DialogActions>
            </Dialog>

            {/* メンバー選択ダイアログ - 外注メンバー */}
            <Dialog
              open={dialogType === 'vendor'}
              onClose={() => setDialogType(null)}
              fullWidth
              maxWidth="sm"
              disableRestoreFocus
            >
              <DialogTitle sx={{
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'grey.50',
                px: 3,
                py: 2
              }}>
                外注メンバー選択
              </DialogTitle>
              <DialogContent sx={{ p: 3 }}>
                {vendors.map(vendor => (
                  <Accordion
                    key={vendor.ID}
                    expanded={expandedVendors.includes(vendor.ID)}
                    onChange={(_, isExpanded) => {
                      setExpandedVendors(prev =>
                        isExpanded
                          ? [...prev, vendor.ID]
                          : prev.filter(id => id !== vendor.ID)
                      );
                    }}
                    sx={{
                      mb: 1,
                      '&.Mui-expanded': {
                        backgroundColor: 'action.hover',
                        mb: 2
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        '&.Mui-expanded': {
                          backgroundColor: 'action.hover',
                          borderBottom: 1,
                          borderColor: 'divider'
                        }
                      }}
                    >
                      <Typography sx={{ fontWeight: 'medium' }}>{vendor.VendorName}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 2 }}>
                      <Grid container spacing={1.5}>
                        {filteredVendorMembers
                          .filter(m => m.VendorID === vendor.ID)
                          .map(member => {
                            const memberDisplayName = `${vendor.VendorName} - ${member.MemberName}`;
                            const isAdded = memberFields.some(field =>
                              field.memberName === memberDisplayName
                            );

                            return (
                              <Grid item xs={4} key={`vendor-${member.ID}`}>
                                <Button
                                  fullWidth
                                  variant={isAdded ? "contained" : "outlined"}
                                  color={isAdded ? "success" : "primary"}
                                  onClick={() => handleVendorMemberSelect(member, vendor.VendorName)}
                                  sx={{
                                    height: '60px',
                                    whiteSpace: 'normal',
                                    lineHeight: 1.2,
                                    textTransform: 'none',
                                    ...(isAdded && {
                                      fontWeight: 'bold',
                                      boxShadow: 1
                                    })
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    align="center"
                                  >
                                    {member.MemberName}
                                  </Typography>
                                </Button>
                              </Grid>
                            );
                          })}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </DialogContent>
              <DialogActions sx={{
                borderTop: 1,
                borderColor: 'divider',
                px: 3,
                py: 2
              }}>
                <Button
                  onClick={() => setDialogType(null)}
                  variant="outlined"
                >
                  閉じる
                </Button>
              </DialogActions>
            </Dialog>

            {/* メンバーリストのヘッダーとデスクトップ表示 */}
            <Box sx={{ mt: 3 }}>
              {/* メンバー詳細表示の折りたたみトグル */}
              {memberFields.length > 0 && (
                <Button
                  fullWidth
                  size="small"
                  onClick={() => setMemberListExpanded(prev => !prev)}
                  endIcon={
                    <ExpandMoreIcon
                      sx={{
                        transform: memberListExpanded ? 'rotate(180deg)' : 'rotate(0)',
                        transition: 'transform 0.3s'
                      }}
                    />
                  }
                  sx={{
                    mb: memberListExpanded ? 1 : 0,
                    color: 'text.secondary',
                    fontWeight: 'normal'
                  }}
                >
                  {memberListExpanded
                    ? `メンバー詳細を隠す (${memberFields.length}人)`
                    : `メンバー詳細を表示 (${memberFields.length}人)`}
                </Button>
              )}
              <Collapse in={memberListExpanded}>
              {/* ヘッダー行 */}
              <Paper
                variant="outlined"
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  mb: 2,
                  overflow: 'hidden'
                }}
              >
                <Grid
                  container
                  spacing={2}
                  sx={{
                    bgcolor: 'grey.50',
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <Grid item sm={3}>
                    <Typography variant="subtitle2" color="text.secondary">メンバー名</Typography>
                  </Grid>
                  <Grid item sm={2}>
                    <Typography variant="subtitle2" color="text.secondary">単価</Typography>
                  </Grid>
                  <Grid item sm={2}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      勤務
                      <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                    </Typography>
                  </Grid>
                  <Grid item sm={2}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      人工
                      <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                    </Typography>
                  </Grid>
                  <Grid item sm={2}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      時間外
                      <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                    </Typography>
                  </Grid>
                  <Grid item sm={1} />
                </Grid>
              </Paper>

              {/* メンバーリスト - デスクトップ表示 */}
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {memberFields.map((field, index) => (
                  <Paper
                    key={field.id}
                    variant="outlined"
                    sx={{
                      mb: 1,
                      '&:hover': {
                        bgcolor: 'grey.50',
                      }
                    }}
                  >
                    <Grid
                      container
                      spacing={2}
                      sx={{
                        py: 2,
                        px: 2,
                      }}
                    >
                      <Grid item sm={3} sx={{
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <Typography noWrap color="text.secondary">{field.memberName}</Typography>
                      </Grid>
                      <Grid item sm={2} sx={{
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <Controller
                          name={`memberData.${index}.baseUnitPrice`}
                          control={control}
                          render={({ field }) => (
                            <Typography color="text.secondary" sx={{ textAlign: 'right' }}>
                              ¥{(field.value || 0).toLocaleString()}
                            </Typography>
                          )}
                        />
                      </Grid>

                      <Grid item sm={2}>
                        <Controller
                          name={`memberData.${index}.workType`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              select
                              fullWidth
                              size="small"
                              onChange={(e) => {
                                const newValue = Number(e.target.value);
                                field.onChange(newValue);
                                promptApplyToAll('workType', newValue, index);
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: 'background.paper',
                                  '&:hover': {
                                    bgcolor: 'action.hover',
                                  }
                                }
                              }}
                            >
                              <MenuItem value={0}>日勤</MenuItem>
                              <MenuItem value={1}>夜勤</MenuItem>
                            </TextField>
                          )}
                        />
                      </Grid>
                      <Grid item sm={2}>
                        <Controller
                          name={`memberData.${index}.isCountTarget`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              select
                              fullWidth
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: 'background.paper',
                                  '&:hover': {
                                    bgcolor: 'action.hover',
                                  }
                                }
                              }}
                            >
                              <MenuItem value={0}>0.00</MenuItem>
                              <MenuItem value={0.25}>0.25</MenuItem>
                              <MenuItem value={0.5}>0.50</MenuItem>
                              <MenuItem value={0.75}>0.75</MenuItem>
                              <MenuItem value={1}>1.00</MenuItem>
                            </TextField>
                          )}
                        />
                      </Grid>
                      <Grid item sm={2}>
                        <Controller
                          name={`memberData.${index}.overtimeHours`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              type="number"
                              fullWidth
                              size="small"
                              name={field.name}
                              inputRef={field.ref}
                              // 0 のときは空表示にして、バックスペース不要で直接入力できるようにする
                              value={field.value || ''}
                              placeholder="0"
                              onChange={(e) => {
                                const v = e.target.value === '' ? 0 : Number(e.target.value);
                                if (!isNaN(v)) field.onChange(v);
                              }}
                              onBlur={(e) => {
                                field.onBlur();
                                const v = Number(e.target.value) || 0;
                                promptApplyToAll('overtimeHours', v, index);
                              }}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">h</InputAdornment>
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: 'background.paper',
                                  '&:hover': {
                                    bgcolor: 'action.hover',
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item sm={1} sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        <IconButton
                          onClick={() => handleRemoveMember(index)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Box>

              {/* メンバーリスト - モバイル表示 */}
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                {memberFields.map((field, index) => {
                  const isExpanded = expandedMemberIds.has(field.id);
                  return (
                    <Paper
                      key={field.id}
                      variant="outlined"
                      sx={{
                        mb: 1.5,
                        overflow: 'hidden'
                      }}
                    >
                      {/* ヘッダー: メンバー名 + 単価 + 削除 */}
                      <Box sx={{
                        px: 1.5,
                        py: 1,
                        bgcolor: 'grey.50',
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                            {field.memberName}
                          </Typography>
                        </Box>
                        <IconButton
                          onClick={() => handleRemoveMember(index)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      {/* 常時表示: 人工（ToggleButtonGroup） - ラベルとボタンを1行に */}
                      <Box sx={{
                        px: 1.5,
                        pt: 1.25,
                        pb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ flexShrink: 0, fontWeight: 'medium' }}
                        >
                          人工<Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                        </Typography>
                        <Controller
                          name={`memberData.${index}.isCountTarget`}
                          control={control}
                          render={({ field: ctrlField }) => (
                            <ToggleButtonGroup
                              value={Number(ctrlField.value)}
                              exclusive
                              onChange={(_, newValue) => {
                                if (newValue !== null) ctrlField.onChange(newValue);
                              }}
                              size="small"
                              sx={{
                                flex: 1,
                                '& .MuiToggleButton-root': {
                                  flex: 1,
                                  py: 0.5,
                                  px: 0,
                                  fontSize: '0.8rem',
                                  fontWeight: 'medium'
                                }
                              }}
                            >
                              <ToggleButton value={1}>1.0</ToggleButton>
                              <ToggleButton value={0.75}>0.75</ToggleButton>
                              <ToggleButton value={0.5}>0.5</ToggleButton>
                              <ToggleButton value={0.25}>0.25</ToggleButton>
                              <ToggleButton value={0}>0</ToggleButton>
                            </ToggleButtonGroup>
                          )}
                        />
                      </Box>

                      {/* 詳細トグル */}
                      <Button
                        fullWidth
                        size="small"
                        onClick={() => toggleMemberExpanded(field.id)}
                        endIcon={
                          <ExpandMoreIcon
                            sx={{
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                              transition: 'transform 0.3s'
                            }}
                          />
                        }
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 'normal',
                          fontSize: '0.8rem',
                          py: 0.5
                        }}
                      >
                        {isExpanded ? '詳細を隠す' : '詳細を表示（勤務・時間外）'}
                      </Button>

                      {/* 折りたたみ: 勤務 + 時間外 */}
                      <Collapse in={isExpanded}>
                        <Box sx={{ px: 1.5, pb: 1.5 }}>
                          <Grid container spacing={1.5}>
                            <Grid item xs={6}>
                              <Controller
                                name={`memberData.${index}.workType`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    select
                                    fullWidth
                                    size="small"
                                    onChange={(e) => {
                                      const newValue = Number(e.target.value);
                                      field.onChange(newValue);
                                      promptApplyToAll('workType', newValue, index);
                                    }}
                                    label={
                                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        勤務
                                        <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                                      </Box>
                                    }
                                    sx={{
                                      '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' }
                                    }}
                                  >
                                    <MenuItem value={0}>日勤</MenuItem>
                                    <MenuItem value={1}>夜勤</MenuItem>
                                  </TextField>
                                )}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <Controller
                                name={`memberData.${index}.overtimeHours`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    type="number"
                                    fullWidth
                                    size="small"
                                    name={field.name}
                                    inputRef={field.ref}
                                    // 0 のときは空表示にして、バックスペース不要で直接入力できるようにする
                                    value={field.value || ''}
                                    placeholder="0"
                                    onChange={(e) => {
                                      const v = e.target.value === '' ? 0 : Number(e.target.value);
                                      if (!isNaN(v)) field.onChange(v);
                                    }}
                                    onBlur={(e) => {
                                      field.onBlur();
                                      const v = Number(e.target.value) || 0;
                                      promptApplyToAll('overtimeHours', v, index);
                                    }}
                                    label={
                                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        時間外
                                        <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                                      </Box>
                                    }
                                    InputProps={{
                                      endAdornment: <InputAdornment position="end">h</InputAdornment>
                                    }}
                                    sx={{
                                      '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' }
                                    }}
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </Paper>
                  );
                })}
              </Box>
              </Collapse>

              {/* 小計表示（1行コンパクト） */}
              <Paper
                variant="outlined"
                sx={{
                  mt: { xs: 1.5, sm: 3 },
                  px: { xs: 1.5, sm: 2 },
                  py: 1,
                  bgcolor: 'grey.50'
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'medium', lineHeight: 1.3, textAlign: 'center' }}>
                  人件費：¥{totalLaborCost.toLocaleString()}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3, textAlign: 'center' }}
                >
                  （{totalLaborHours.toFixed(2)}人工、時間外{totalOvertimeHours.toFixed(2)}時間）
                </Typography>
              </Paper>
            </Box>
          </Paper>
        </Grid>

        {/* 車両情報セクション */}
        <Grid item xs={12}>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.5, sm: 3 },
              mb: { xs: 2, sm: 3 },
              bgcolor: 'background.default',
              borderRadius: 1
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                pb: 1,
                borderBottom: 2,
                borderColor: 'primary.main',
                color: 'primary.main',
                fontWeight: 'medium'
              }}
            >
              車両情報
            </Typography>

            {/* 車両選択セクション */}
            <Paper
              variant="outlined"
              sx={{
                mb: 3,
                p: 2,
                bgcolor: 'grey.50'
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 2,
                  fontWeight: 'medium'
                }}
              >
                車両選択
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setDialogType('car')}
                startIcon={<DirectionsCarIcon />}
                sx={{
                  py: 1.5,
                  bgcolor: 'background.paper',
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
              >
                車両を選択
              </Button>
            </Paper>

            {/* 車両リスト */}
            <Box sx={{ mt: 3 }}>
              {/* ヘッダー行 - デスクトップ */}
              <Paper
                variant="outlined"
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  mb: 2,
                  overflow: 'hidden'
                }}
              >
                <Grid
                  container
                  spacing={2}
                  sx={{
                    bgcolor: 'grey.50',
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <Grid item sm={2}>
                    <Typography variant="subtitle2" color="text.secondary">車両名</Typography>
                  </Grid>
                  <Grid item sm={2}>
                    <Typography variant="subtitle2" color="text.secondary">ナンバー</Typography>
                  </Grid>
                  <Grid item sm={2}>
                    <Typography variant="subtitle2" color="text.secondary">車両費</Typography>
                  </Grid>
                  <Grid item sm={2}>
                    <Typography variant="subtitle2" color="text.secondary">走行距離(片道)</Typography>
                  </Grid>
                  <Grid item sm={2}>
                    <Typography variant="subtitle2" color="text.secondary">有料道路料金(片道)</Typography>
                  </Grid>
                  <Grid item sm={2} />
                </Grid>
              </Paper>

              {/* 車両リスト - デスクトップ表示 */}
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {vehicleFields.map((field, index) => {
                  const car = cars.find(c => c.ID === field.carId);
                  return (
                    <Paper
                      key={field.id}
                      variant="outlined"
                      sx={{
                        mb: 1,
                        '&:hover': {
                          bgcolor: 'grey.50',
                        }
                      }}
                    >
                      <Grid
                        container
                        spacing={2}
                        sx={{
                          py: 2,
                          px: 2,
                        }}
                      >
                        <Grid item sm={2} sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography>{car?.Car_Nickname || car?.Car_Model || '不明'}</Typography>
                        </Grid>
                        <Grid item sm={2} sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography>{car?.Car_Number_4 || '-'}</Typography>
                        </Grid>
                        <Grid item sm={2} sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography>¥{(field.unitPrice || 0).toLocaleString()}</Typography>
                        </Grid>
                        <Grid item sm={2}>
                          <TextField
                            {...register(`vehicleData.${index}.mileage`)}
                            type="number"
                            size="small"
                            fullWidth
                            onChange={(e) => {
                              // 空文字列チェック
                              if (e.target.value === '') {
                                setValue(`vehicleData.${index}.mileage`, 0);
                              } else {
                                // 数値変換とNaNチェック
                                const value = Number(e.target.value);
                                if (!isNaN(value)) {
                                  setValue(`vehicleData.${index}.mileage`, value);
                                }
                              }
                            }}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">km</InputAdornment>
                            }}
                          />
                        </Grid>
                        <Grid item sm={2}>
                          <NumberTextField
                            {...register(`vehicleData.${index}.tollFee`)}
                            value={watch(`vehicleData.${index}.tollFee`) || ''}
                            thousandSeparator={true}
                            fullWidth
                            size="small"
                            onChange={(e) => {
                              const value = e.target.value === '' ? 0 : Number(e.target.value);
                              if (!isNaN(value)) {
                                setValue(`vehicleData.${index}.tollFee`, value);
                              }
                            }}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">¥</InputAdornment>
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                bgcolor: 'background.paper'
                              }
                            }}
                          />
                        </Grid>
                        <Grid item sm={2} sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}>
                          <IconButton
                            onClick={() => removeVehicle(index)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Paper>
                  );
                })}
              </Box>

              {/* モバイル表示 */}
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                {vehicleFields.map((field, index) => {
                  const car = cars.find(c => c.ID === field.carId);
                  return (
                    <Paper
                      key={field.id}
                      variant="outlined"
                      sx={{
                        mb: 1.5,
                        overflow: 'hidden'
                      }}
                    >
                      {/* ヘッダー: 車名・ナンバー・車両費・削除 を1行に */}
                      <Box sx={{
                        px: 1.5,
                        py: 1,
                        bgcolor: 'grey.50',
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 'medium',
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {car?.Car_Nickname || car?.Car_Model || '不明'}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                        >
                          {car?.Car_Number_4 || '-'}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ flexShrink: 0, whiteSpace: 'nowrap', fontWeight: 'medium' }}
                        >
                          ¥{(field.unitPrice || 0).toLocaleString()}
                        </Typography>
                        <IconButton
                          onClick={() => removeVehicle(index)}
                          color="error"
                          size="small"
                          sx={{ flexShrink: 0 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Box sx={{ px: 1.5, py: 1.25 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <NumberTextField
                              {...register(`vehicleData.${index}.mileage`)}
                              label="走行距離(片道)"
                              value={watch(`vehicleData.${index}.mileage`) || ''}
                              thousandSeparator={false} // 走行距離はカンマ区切りなしでも良いでしょう
                              fullWidth
                              size="small"
                              onChange={(e) => {
                                // 空文字列の場合は0を設定
                                if (e.target.value === '') {
                                  setValue(`vehicleData.${index}.mileage`, 0);
                                  return;
                                }

                                // 数値変換して設定
                                const parsedValue = Number(e.target.value);
                                if (!isNaN(parsedValue)) {
                                  setValue(`vehicleData.${index}.mileage`, parsedValue);
                                }
                              }}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">km</InputAdornment>
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: 'background.paper'
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <NumberTextField
                              {...register(`vehicleData.${index}.tollFee`)}
                              label="有料道路料金(片道)"
                              value={watch(`vehicleData.${index}.tollFee`) || ''}
                              thousandSeparator={true}
                              fullWidth
                              size="small"
                              onChange={(e) => {

                                const value = e.target.value;

                                // 空文字列の場合は0を設定
                                if (value === '') {
                                  setValue(`vehicleData.${index}.tollFee`, 0);
                                  return;
                                }

                                // 数値変換して設定
                                const parsedValue = Number(value);
                                if (!isNaN(parsedValue)) {
                                  setValue(`vehicleData.${index}.tollFee`, parsedValue);
                                }
                              }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">¥</InputAdornment>
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: 'background.paper'
                                }
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* 備考 */}
        <Grid item xs={12}>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.5, sm: 3 },
              mb: { xs: 2, sm: 3 },
              bgcolor: 'background.default',
              borderRadius: 1
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                pb: 1,
                borderBottom: 2,
                borderColor: 'primary.main',
                color: 'primary.main',
                fontWeight: 'medium'
              }}
            >
              備考
            </Typography>

            <Box sx={{ mb: 3 }}>
              {remarksFields.map((field, index) => (
                <Paper
                  key={field.id}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    p: 2,
                    '&:hover': {
                      bgcolor: 'grey.50'
                    }
                  }}
                >
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={11}>
                      <TextField
                        {...register(`remarkData.${index}.remarks`)}
                        fullWidth
                        multiline
                        rows={2}
                        label="備考"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'background.paper',
                            '&:hover': {
                              bgcolor: 'action.hover'
                            }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                      <IconButton
                        onClick={() => removeRemarks(index)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Box>

            <Button
              startIcon={<AddIcon />}
              onClick={() => appendRemarks({
                remarksOrder: remarksFields.length + 1,
                remarks: ''
              })}
              variant="outlined"
              sx={{
                mb: 2,
                bgcolor: 'background.paper',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              備考を追加
            </Button>
          </Paper>
        </Grid>

        {/* 工事完了チェックボックス（保存ボタン上 / 矩形全体クリック可） */}
        <Grid item xs={12}>
          <ButtonBase
            component="div"
            role="checkbox"
            aria-checked={isConstructionCompleted}
            onClick={() => setIsConstructionCompleted(prev => !prev)}
            sx={{
              mt: 2,
              width: '100%',
              bgcolor: 'grey.50',
              border: 1,
              borderColor: 'primary.main',
              borderRadius: 1,
              py: { xs: 1.25, sm: 1.5 },
              px: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1,
              transition: 'background-color 0.2s',
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            <Checkbox
              checked={isConstructionCompleted}
              color="primary"
              disableRipple
              tabIndex={-1}
              sx={{
                '& .MuiSvgIcon-root': { fontSize: 28 },
                pointerEvents: 'none',
                p: 0
              }}
            />
            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
              {isConstructionCompleted ? '工事完了' : '工事未完了'}
            </Typography>
          </ButtonBase>
        </Grid>

        {/* 保存ボタン */}
        <Grid item xs={12}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            mt: 1
          }}>
            <Button
              variant="outlined"
              size="large"
              onClick={onCancel}
              sx={{ minWidth: 120 }}
            >
              キャンセル
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              sx={{ minWidth: 120 }}
            >
              保存
            </Button>
          </Box>
        </Grid>

        {/* 「他のメンバーにも適用しますか？」ダイアログ */}
        <Dialog
          open={Boolean(applyToAllDialog?.open)}
          onClose={handleApplyToAllCancel}
          maxWidth="xs"
          fullWidth
          disableRestoreFocus
        >
          <DialogTitle>他のメンバーにも適用しますか？</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {applyToAllDialog?.fieldName === 'workType'
                ? `勤務を「${Number(applyToAllDialog?.value) === 1 ? '夜勤' : '日勤'}」に変更しました。`
                : `時間外を「${applyToAllDialog?.value ?? 0} h」に変更しました。`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              他の {Math.max(0, memberFields.length - 1)} 人のメンバーにも同じ値を適用しますか？
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1.5 }}>
            <Button onClick={handleApplyToAllCancel} color="inherit">
              キャンセル
            </Button>
            <Button onClick={handleApplyToAllConfirm} variant="contained" color="primary">
              全員に適用
            </Button>
          </DialogActions>
        </Dialog>

        {/* 車両選択ダイアログ */}
        <Dialog
          open={dialogType === 'car'}
          onClose={() => setDialogType(null)}
          fullWidth
          maxWidth="sm"
          disableRestoreFocus
        >
          <DialogTitle sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
            px: 3,
            py: 2
          }}>
            車両選択
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={1.5}>
              {cars.map((car) => {
                const isAdded = vehicleFields.some((field) =>
                  field.carId === car.ID
                );

                return (
                  <Grid item xs={6} sm={4} key={`car-${car.ID}`}>
                    <Button
                      fullWidth
                      variant={isAdded ? "contained" : "outlined"}
                      color={isAdded ? "success" : "primary"}
                      onClick={() => handleCarSelect(car)}
                      sx={{
                        height: { xs: '70px', sm: '60px' },
                        minHeight: { xs: '70px', sm: '60px' },
                        p: { xs: 1.5, sm: 1 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        ...(isAdded && {
                          fontWeight: 'bold',
                          boxShadow: 1
                        })
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: { xs: '0.85rem', sm: '0.875rem' },
                          lineHeight: 1.2,
                          width: '100%',
                          mb: 0.5,
                          // 2行までは表示し、それ以上は省略
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          whiteSpace: 'normal'
                        }}
                      >
                        {car.Car_Nickname || car.Car_Model}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {car.Car_Number_4}
                      </Typography>
                    </Button>
                  </Grid>
                );
              })}
            </Grid>
          </DialogContent>
          <DialogActions sx={{
            borderTop: 1,
            borderColor: 'divider',
            px: 3,
            py: 2
          }}>
            <Button
              onClick={() => setDialogType(null)}
              variant="outlined"
            >
              閉じる
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>

    </form>
  );
};